/**
 * extracted-evidence — typed projection for per-fact contract citations.
 *
 * The backend serializes an `eqCardsRaw` envelope onto the getContract
 * response (render-cards.ts pickCitation → contracts.ts eqCardsRaw
 * passthrough). Each card carries a `facts[]` array where every fact is
 * a {label, value, confidence?, citation?:{page, snippet}} — the snippet
 * is the verbatim matching_text excerpt the analyzer pulled from the PDF.
 *
 * `eqCardsRaw` is NOT on the @equaliq/api-model GetContractResponseContent
 * stub (it is `[key: string]: unknown`). Per frontend/.rules/002 we read
 * it as a TYPED PROJECTION cast at the boundary — NO api-model bump. This
 * mirrors the `pdfUrl` cast in contract-editor.tsx:198.
 *
 * Maps from: GetContractResponseContent.eqCardsRaw (backend EqCardSet).
 */
import type { GetContractResponseContent } from "src/types/contract.types"

export interface CardFactCitation {
  page?: number
  snippet?: string
}

export interface CardFact {
  label: string
  value: string
  confidence?: number
  citation?: CardFactCitation
}

export interface EvidenceCardGroup {
  /** The card's human label (e.g. "Money You'll Receive"). */
  cardLabel: string
  /** The raw card key (moneyYouReceive, whatYouOwn, …) — stable identity. */
  cardKey: string
  facts: CardFact[]
}

/** Shape of the eqCardsRaw envelope the backend serializes. */
interface RawEvidenceCard {
  key?: string
  label?: string
  title?: string
  facts?: unknown
}

interface RawEqCardSet {
  cards?: Record<string, RawEvidenceCard> | RawEvidenceCard[]
}

// Human labels keyed by the backend card key. Falls back to the raw key
// when the envelope omits a label, so a new card key the backend adds
// still renders (weakest-correct: no hard-coded exhaustive set required).
const CARD_KEY_LABELS: Record<string, string> = {
  money: "Money You'll Receive",
  moneyYouReceive: "Money You'll Receive",
  ownership: "What You Own",
  whatYouOwn: "What You Own",
  responsibilities: "What You're Responsible For",
  whatYoureResponsibleFor: "What You're Responsible For",
  duration: "How Long This Deal Lasts",
  howLongThisDealLasts: "How Long This Deal Lasts",
  legal: "Risks, Costs & Legal Stuff",
  risksCostsLegalStuff: "Risks, Costs & Legal Stuff",
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

const coerceCitation = (raw: unknown): CardFactCitation | undefined => {
  if (!isRecord(raw)) return undefined
  const page = typeof raw.page === "number" ? raw.page : undefined
  const snippet =
    typeof raw.snippet === "string" && raw.snippet.trim().length > 0
      ? raw.snippet
      : undefined
  if (page === undefined && snippet === undefined) return undefined
  return { page, snippet }
}

const coerceFact = (raw: unknown): CardFact | null => {
  if (!isRecord(raw)) return null
  const label = typeof raw.label === "string" ? raw.label : ""
  const value = typeof raw.value === "string" ? raw.value : ""
  // A fact with neither a label nor a value carries no information.
  if (!label && !value) return null
  return {
    label,
    value,
    confidence: typeof raw.confidence === "number" ? raw.confidence : undefined,
    citation: coerceCitation(raw.citation),
  }
}

const coerceCardLabel = (card: RawEvidenceCard, fallbackKey: string): string =>
  card.label?.trim() ||
  card.title?.trim() ||
  CARD_KEY_LABELS[card.key ?? fallbackKey] ||
  CARD_KEY_LABELS[fallbackKey] ||
  fallbackKey

/**
 * Project the contract response's eqCardsRaw envelope into a flat list of
 * evidence card groups (one per card, each carrying its facts[]). Reads
 * via a cast at the boundary — defends every field so a partial / drifted
 * envelope degrades to value-only facts or an empty list, never a throw.
 *
 * Returns [] when there is no eqCardsRaw (e.g. demo contracts, which carry
 * only eqData) so the panel can render an honest empty state.
 */
export const projectExtractedEvidence = (
  contract?: GetContractResponseContent | null
): EvidenceCardGroup[] => {
  const raw = (contract as { eqCardsRaw?: RawEqCardSet } | null | undefined)
    ?.eqCardsRaw
  const cards = raw?.cards
  if (!cards) return []

  const entries: Array<[string, RawEvidenceCard]> = Array.isArray(cards)
    ? cards.map((card, index): [string, RawEvidenceCard] => [
        card.key ?? String(index),
        card,
      ])
    : Object.entries(cards)

  const groups: EvidenceCardGroup[] = []

  entries.forEach(([key, card]) => {
    if (typeof card !== "object" || card === null) return
    const factsRaw = Array.isArray(card.facts) ? card.facts : []
    const facts = factsRaw
      .map(coerceFact)
      .filter((fact): fact is CardFact => fact !== null)
    if (facts.length === 0) return
    groups.push({
      cardKey: card.key ?? key,
      cardLabel: coerceCardLabel(card, key),
      facts,
    })
  })

  return groups
}

/** True when the contract carries at least one extracted fact. */
export const hasExtractedEvidence = (
  contract?: GetContractResponseContent | null
): boolean => projectExtractedEvidence(contract).length > 0
